/**
 * Conditional visibility ("show when") for program phase admin tasks.
 *
 * ONE shared evaluation used by every consumer — the phase-action
 * instances endpoint, the completion endpoint, the workflow snapshot's
 * Required-gate fold (summariseAdminTasks), and the drawer panel — so
 * the rule can never drift between the screen and the gate.
 *
 * Semantics (owner directive, 2026-10-27):
 *  - A definition may carry `showWhen: { sourceActionDefId, values }`.
 *    The task is VISIBLE for an applicant only when the source task
 *    (same program, same phase) has a stored answer and that answer is
 *    one of `values`.
 *  - Options are carried DYNAMICALLY: `values` stores the source's
 *    option VALUES (option ids for select_one_option; 'yes'/'no' for
 *    yes_no) — never label text, never free text — because yes_no
 *    labels are configured per task and option lists are arbitrary.
 *    Labels are read from the source definition's CURRENT config at
 *    display time only.
 *  - Visibility is DERIVED at read time from sibling instances' stored
 *    answers. Nothing about visibility is persisted on instances.
 *  - FAIL OPEN on broken conditions: if the source definition is
 *    missing, is not an answer-bearing type, or a stored value (the
 *    condition's values, or the source's stored answer) no longer
 *    exists among the source's current options, the task is VISIBLE
 *    and flagged `broken` so the editor can surface the problem. A
 *    task must never silently hide forever.
 *  - The one exception: a source that simply has NOT BEEN ANSWERED YET
 *    hides its dependents — that is the normal state (the route has
 *    not been chosen yet), not a broken condition.
 */

export interface ShowWhenDefLike {
  id?: string;
  actionType?: string;
  title?: string;
  phaseKey?: string;
  sortOrder?: number;
  config?: {
    options?: Array<{ id?: string; label?: string }> | null;
    yesLabel?: string;
    noLabel?: string;
    [key: string]: any;
  } | null;
  showWhen?: { sourceActionDefId?: string; values?: string[] } | null;
}

export interface ShowWhenInstanceLike {
  actionDefId?: string;
  selectedOptionId?: string;
  savedAnswer?: string;
}

export interface ShowWhenVerdict {
  visible: boolean;
  broken: boolean;
}

/**
 * The answer values a definition can store, with their CURRENT display
 * labels — the dynamic option source for every picker and preview.
 * Returns null when the definition's type bears no discrete answer
 * (only select_one_option and yes_no can be show-when sources).
 */
export function showWhenAnswerValues(
  def: ShowWhenDefLike | null | undefined
): Array<{ value: string; label: string }> | null {
  if (!def) return null;
  if (def.actionType === 'select_one_option') {
    const opts = Array.isArray(def.config?.options) ? def.config!.options! : [];
    return opts
      .filter((o) => o && typeof o.id === 'string' && o.id)
      .map((o) => ({ value: String(o.id), label: String(o.label || o.id) }));
  }
  if (def.actionType === 'yes_no') {
    return [
      { value: 'yes', label: String(def.config?.yesLabel || 'Yes') },
      { value: 'no', label: String(def.config?.noLabel || 'No') },
    ];
  }
  return null;
}

/** The discrete answer stored on an instance of an answer-bearing def. */
export function storedAnswerOf(
  def: ShowWhenDefLike | null | undefined,
  instance: ShowWhenInstanceLike | null | undefined
): string | null {
  if (!def || !instance) return null;
  if (def.actionType === 'select_one_option') {
    const v = instance.selectedOptionId;
    return typeof v === 'string' && v ? v : null;
  }
  if (def.actionType === 'yes_no') {
    const v = instance.savedAnswer;
    return v === 'yes' || v === 'no' ? v : null;
  }
  return null;
}

/**
 * Evaluate one definition's visibility for one applicant, given the
 * program's definitions and that applicant's instances (both keyed by
 * action definition id).
 */
export function evaluateTaskVisibility(
  def: ShowWhenDefLike | null | undefined,
  defsById: ReadonlyMap<string, ShowWhenDefLike>,
  instancesByDefId: ReadonlyMap<string, ShowWhenInstanceLike>
): ShowWhenVerdict {
  const cond = def?.showWhen;
  if (!cond || !cond.sourceActionDefId) return { visible: true, broken: false };

  const sourceDef = defsById.get(cond.sourceActionDefId);
  if (!sourceDef) return { visible: true, broken: true };

  const answerValues = showWhenAnswerValues(sourceDef);
  if (!answerValues) return { visible: true, broken: true };

  const validValues = new Set(answerValues.map((v) => v.value));
  const values = Array.isArray(cond.values)
    ? cond.values.filter((v): v is string => typeof v === 'string' && !!v)
    : [];
  // A stored condition value that the source no longer offers means
  // the configuration drifted after save — fail open, flag broken.
  if (values.length === 0 || values.some((v) => !validValues.has(v))) {
    return { visible: true, broken: true };
  }

  const sourceInstance = sourceDef.id ? instancesByDefId.get(sourceDef.id) : undefined;
  const answer = storedAnswerOf(sourceDef, sourceInstance);
  // Not answered yet: the route has not been chosen — hidden, normal.
  if (answer == null) return { visible: false, broken: false };
  // The stored answer itself no longer exists among the source's
  // current options (the picked option was deleted) — fail open.
  if (!validValues.has(answer)) return { visible: true, broken: true };

  return { visible: values.includes(answer), broken: false };
}

/**
 * The configuration problem with one definition's showWhen condition,
 * or null when the condition is well-formed against the given def set.
 * Shared by the save-time route validation and the editor's broken
 * warning so both apply exactly the same rules.
 */
export function showWhenProblemFor(
  def: ShowWhenDefLike,
  defsById: ReadonlyMap<string, ShowWhenDefLike>
): string | null {
  const cond = def?.showWhen;
  if (!cond) return null;
  const title = def.title || def.id || 'Task';
  const srcId = cond.sourceActionDefId;
  if (!srcId || typeof srcId !== 'string') {
    return `"${title}": the show-when condition is missing its source task.`;
  }
  if (def.id && srcId === def.id) {
    return `"${title}": a task cannot be shown based on its own answer.`;
  }
  const source = defsById.get(srcId);
  if (!source) {
    return `"${title}": the show-when source task no longer exists.`;
  }
  if (source.phaseKey !== def.phaseKey) {
    return `"${title}": the show-when source task must be in the same phase.`;
  }
  const answerValues = showWhenAnswerValues(source);
  if (!answerValues) {
    return `"${title}": the show-when source task must be a "Select One Option" or "Yes / No" task.`;
  }
  if (source.showWhen) {
    return `"${title}": the show-when source task cannot itself be conditional (conditions are one level deep).`;
  }
  if ((Number(source.sortOrder) || 0) >= (Number(def.sortOrder) || 0)) {
    return `"${title}": the show-when source task must come earlier in the task order.`;
  }
  const values = Array.isArray(cond.values) ? cond.values : [];
  if (values.length === 0) {
    return `"${title}": choose at least one answer value for the show-when condition.`;
  }
  const valid = new Set(answerValues.map((v) => v.value));
  const stale = values.filter((v) => !valid.has(v));
  if (stale.length > 0) {
    return `"${title}": the show-when condition names answer value(s) the source task no longer offers: ${stale.join(', ')}.`;
  }
  return null;
}

/** Save-time validation for a whole program def set: one error per bad condition. */
export function validateShowWhenConditions(defs: ShowWhenDefLike[]): string[] {
  const defsById = new Map<string, ShowWhenDefLike>();
  for (const d of defs || []) {
    if (d && d.id) defsById.set(d.id, d);
  }
  const errors: string[] = [];
  for (const def of defs || []) {
    if (!def?.showWhen) continue;
    const problem = showWhenProblemFor(def, defsById);
    if (problem) errors.push(problem);
  }
  return errors;
}

/**
 * Plain-language preview of a condition, built from the source's
 * CURRENT labels — e.g. `Shown when 'Decision: What happens next?' is
 * RETURN for corrections or STOP, not eligible`. Null when there is no
 * (resolvable) condition to describe.
 */
export function describeShowWhen(
  def: ShowWhenDefLike | null | undefined,
  sourceDef: ShowWhenDefLike | null | undefined
): string | null {
  const cond = def?.showWhen;
  if (!cond || !cond.sourceActionDefId || !sourceDef) return null;
  const answerValues = showWhenAnswerValues(sourceDef);
  if (!answerValues) return null;
  const labelByValue = new Map(answerValues.map((v) => [v.value, v.label]));
  const chosen = (Array.isArray(cond.values) ? cond.values : [])
    .map((v) => labelByValue.get(v))
    .filter((l): l is string => !!l);
  if (chosen.length === 0) return null;
  return `Shown when '${sourceDef.title || 'the source task'}' is ${chosen.join(' or ')}`;
}
